import Phaser from 'phaser';

const SKY_TOP = 0x1a1033;
const SKY_MID = 0x4c0519;
const SKY_BOT = 0xea580c;

const RIFT_PURPLE = 0x7c2d92;
const RIFT_PINK = 0xdb2777;
const RIFT_GOLD = 0xfbbf24;
const RIFT_FLAME = 0xf97316;

const DEBRIS_DARK = 0x1c1917;
const DEBRIS_EDGE = 0x7c3aed;

const STAR = 0xfde68a;

export function drawDarkDimensionSkyBackground(
  g: Phaser.GameObjects.Graphics,
  w: number,
  h: number,
) {
  g.clear();
  const step = Math.max(1, Math.floor(h / 40));
  for (let y = 0; y < h; y += step) {
    const t = y / h;
    let r: number, gC: number, b: number;
    if (t < 0.5) {
      const u = t / 0.5;
      r = lerp((SKY_TOP >> 16) & 0xff, (SKY_MID >> 16) & 0xff, u);
      gC = lerp((SKY_TOP >> 8) & 0xff, (SKY_MID >> 8) & 0xff, u);
      b = lerp(SKY_TOP & 0xff, SKY_MID & 0xff, u);
    } else {
      const u = (t - 0.5) / 0.5;
      r = lerp((SKY_MID >> 16) & 0xff, (SKY_BOT >> 16) & 0xff, u);
      gC = lerp((SKY_MID >> 8) & 0xff, (SKY_BOT >> 8) & 0xff, u);
      b = lerp(SKY_MID & 0xff, SKY_BOT & 0xff, u);
    }
    g.fillStyle((r << 16) | (gC << 8) | b, 1);
    g.fillRect(0, y, w, step + 1);
  }

  const rand = mulberry32(1313);
  for (let i = 0; i < 36; i++) {
    const sx = rand() * w;
    const sy = rand() * h * 0.75;
    const sr = rand() < 0.2 ? 1.8 : 1;
    g.fillStyle(STAR, 0.75);
    g.fillCircle(sx, sy, sr);
  }

  drawRiftCrack(g, w * 0.22, h * 0.18, w * 0.4, 60, -0.3);
  drawRiftCrack(g, w * 0.68, h * 0.32, w * 0.35, 45, 0.35);
  drawRiftCrack(g, w * 0.45, h * 0.58, w * 0.3, 38, 0.1);
}

export function drawDarkDimensionSilhouette(
  g: Phaser.GameObjects.Graphics,
  w: number,
  h: number,
) {
  const rand = mulberry32(1414);
  const baselineY = Math.floor(h * 0.82);

  g.fillStyle(DEBRIS_DARK, 0.9);
  g.fillRect(0, baselineY, w, h - baselineY);

  for (let i = 0; i < 9; i++) {
    const cx = rand() * w;
    const cy = baselineY - 40 - rand() * (baselineY * 0.5);
    const sides = 4 + Math.floor(rand() * 3);
    const size = 14 + rand() * 36;
    const rotation = rand() * Math.PI * 2;

    g.fillStyle(DEBRIS_DARK, 0.95);
    g.lineStyle(2, DEBRIS_EDGE, 0.7);
    drawJaggedShard(g, cx, cy, size, sides, rotation);
  }

  for (let i = 0; i < 4; i++) {
    const cx = rand() * w;
    const cy = rand() * baselineY;
    g.fillStyle(RIFT_GOLD, 0.5);
    g.fillCircle(cx, cy, 2 + rand() * 2);
  }
}

function drawRiftCrack(
  g: Phaser.GameObjects.Graphics,
  cx: number,
  cy: number,
  length: number,
  height: number,
  rotation: number,
) {
  const pts: { x: number; y: number }[] = [];
  const segments = 22;
  for (let i = 0; i <= segments; i++) {
    const t = i / segments;
    const baseX = (t - 0.5) * length;
    const bulge = Math.sin(t * Math.PI) * height;
    const x = baseX * Math.cos(rotation) - bulge * Math.sin(rotation);
    const y = baseX * Math.sin(rotation) + bulge * Math.cos(rotation);
    pts.push({ x: cx + x, y: cy + y });
  }
  const mirrored = [...pts];
  for (let i = segments; i >= 0; i--) {
    const t = i / segments;
    const baseX = (t - 0.5) * length;
    const bulge = -Math.sin(t * Math.PI) * height;
    const x = baseX * Math.cos(rotation) - bulge * Math.sin(rotation);
    const y = baseX * Math.sin(rotation) + bulge * Math.cos(rotation);
    mirrored.push({ x: cx + x, y: cy + y });
  }

  g.fillStyle(RIFT_PURPLE, 0.35);
  g.beginPath();
  g.moveTo(mirrored[0].x, mirrored[0].y);
  for (let i = 1; i < mirrored.length; i++) g.lineTo(mirrored[i].x, mirrored[i].y);
  g.closePath();
  g.fillPath();

  g.fillStyle(RIFT_PINK, 0.5);
  const innerScale = 0.65;
  const inner: { x: number; y: number }[] = [];
  for (const p of mirrored) {
    inner.push({
      x: cx + (p.x - cx) * innerScale,
      y: cy + (p.y - cy) * innerScale,
    });
  }
  g.beginPath();
  g.moveTo(inner[0].x, inner[0].y);
  for (let i = 1; i < inner.length; i++) g.lineTo(inner[i].x, inner[i].y);
  g.closePath();
  g.fillPath();

  g.lineStyle(2, RIFT_FLAME, 0.8);
  g.beginPath();
  for (let i = 0; i < pts.length; i++) {
    if (i === 0) g.moveTo(pts[i].x, pts[i].y);
    else g.lineTo(pts[i].x, pts[i].y);
  }
  g.strokePath();

  g.fillStyle(RIFT_GOLD, 1);
  g.fillCircle(pts[0].x, pts[0].y, 2.5);
  g.fillCircle(pts[pts.length - 1].x, pts[pts.length - 1].y, 2.5);
}

function drawJaggedShard(
  g: Phaser.GameObjects.Graphics,
  cx: number,
  cy: number,
  size: number,
  sides: number,
  rotation: number,
) {
  const pts: { x: number; y: number }[] = [];
  for (let i = 0; i < sides; i++) {
    const a = rotation + (i / sides) * Math.PI * 2;
    const r = size * (0.7 + Math.sin(a * 3) * 0.3);
    pts.push({ x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) });
  }
  g.beginPath();
  g.moveTo(pts[0].x, pts[0].y);
  for (let i = 1; i < pts.length; i++) g.lineTo(pts[i].x, pts[i].y);
  g.closePath();
  g.fillPath();
  g.strokePath();
}

function lerp(a: number, b: number, t: number): number {
  return Math.round(a + (b - a) * t);
}

function mulberry32(seed: number) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
